<template>
  <div class="cpo-overlay" @mousedown.self="$emit('close')">
    <div class="cpo-modal" role="dialog" aria-modal="true">
      <header class="cpo-header">
        <h3 class="cpo-title">
          {{ isEdit ? 'Edit custom option' : 'Custom payment option' }}
          <span v-if="patientName" class="cpo-title-sub">for {{ patientName }}</span>
        </h3>
        <button class="cpo-x" type="button" @click="$emit('close')" title="Close">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <div class="cpo-body">
        <p class="cpo-intro">
          A one-off plan just for this patient. It won't change your practice-wide
          payment options.
        </p>

        <label class="cpo-field">
          <span class="cpo-label">Title <span class="cpo-req">*</span></span>
          <input
            ref="titleInput"
            v-model.trim="form.title"
            type="text"
            class="cpo-input"
            maxlength="60"
            placeholder="e.g., Custom 4-payment plan"
            @keydown.enter.prevent="onSave"
          />
        </label>

        <label class="cpo-field">
          <span class="cpo-label">Description</span>
          <textarea
            v-model="form.desc"
            class="cpo-input cpo-textarea"
            rows="2"
            maxlength="200"
            placeholder="Short description the patient sees on the option card"
          ></textarea>
        </label>

        <label class="cpo-field">
          <span class="cpo-label">Terms / conditions</span>
          <input
            v-model="form.terms"
            type="text"
            class="cpo-input"
            maxlength="160"
            placeholder="Optional — shown in small print below the card"
          />
        </label>
      </div>

      <footer class="cpo-footer">
        <button class="cpo-btn cpo-btn-ghost" type="button" @click="$emit('close')">Cancel</button>
        <button class="cpo-btn cpo-btn-primary" type="button" :disabled="!canSave" @click="onSave">
          <i class="fa fa-check"></i>
          {{ isEdit ? 'Save changes' : 'Add option' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomPaymentOptionModal',
  props: {
    patientName: { type: String, default: '' },
    // When set, the modal is in edit mode and pre-fills from this option.
    option: { type: Object, default: null },
  },
  data() {
    return {
      form: {
        title: (this.option && this.option.title) || '',
        desc: (this.option && this.option.desc) || '',
        terms: (this.option && this.option.terms) || '',
      },
    };
  },
  computed: {
    isEdit() { return !!(this.option && this.option.id); },
    canSave() { return this.form.title.trim().length > 0; },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.titleInput) this.$refs.titleInput.focus();
    });
  },
  methods: {
    onSave() {
      if (!this.canSave) return;
      this.$emit('save', {
        id: this.isEdit ? this.option.id : null,
        title: this.form.title.trim(),
        desc: (this.form.desc || '').trim(),
        terms: (this.form.terms || '').trim(),
      });
    },
  },
};
</script>

<style scoped>
.cpo-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
}
.cpo-modal {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
}
.cpo-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef1f4;
}
.cpo-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0d5f54;
  line-height: 1.3;
}
.cpo-title-sub {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  margin-top: 2px;
}
.cpo-x {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 16px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
}
.cpo-x:hover { background: #f1f3f6; color: #475569; }

.cpo-body { padding: 16px 20px 4px; }
.cpo-intro {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 14px;
}
.cpo-field { display: block; margin-bottom: 14px; }
.cpo-label {
  display: block;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  margin-bottom: 5px;
}
.cpo-req { color: #dc2626; }
.cpo-input {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  box-sizing: border-box;
}
.cpo-input:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.cpo-textarea { resize: vertical; min-height: 52px; line-height: 1.5; }

.cpo-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 18px;
}
.cpo-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 7px;
  cursor: pointer;
  font-family: inherit;
  border: 1px solid transparent;
}
.cpo-btn-ghost {
  background: #fff;
  border-color: #e4e7ec;
  color: #475569;
}
.cpo-btn-ghost:hover { background: #f8fafc; }
.cpo-btn-primary {
  background: #0d5f54;
  color: #fff;
}
.cpo-btn-primary:hover:not(:disabled) { background: #094840; }
.cpo-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.cpo-btn .fa { font-size: 11px; }
</style>
